
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import AdUnit from '@/components/AdUnit.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import Claim from '@/components/sports/all/Claim.vue';
import CompetitionList from '@/components/sports/all/CompetitionList.vue';
import SportCard from '@/components/sports/all/SportCard.vue';
import SportHeader from '@/components/sports/common/SportHeader.vue';
import SportsLayout from '@/components/sports/common/SportsLayout.vue';
import Subheading from '@/components/sports/common/Subheading.vue';
import EventCard from '@/components/sports/common/event/EventCard.vue';

import { Platform, SportEventsEdgeV2, SportType } from '@/@types/graphql-types';
import { WebLocale } from '@/enums/web-locale';

import {
	GetAllSportsPageV2Document,
	GetAllSportsPageV2QueryVariables,
} from '@/pages/graphql/queries/GetAllSportsPageV2.query';

import { getOverviewUrlBySport } from '@/routing/sports';

import SponsoredRecommendationSports from '@/components/sponsored-recommendations/SponsoredRecommendationSports.vue';
import SrSportsList from '@/components/sponsored-recommendations/SponsoredRecommendationSportsList.vue';
import ProvideUpcomingSportEvents from '@/components/sports/single-step/ProvideUpcomingSportEvents.vue';

import { SportLocale } from '@/enums/sports';
import { eventsEdgeContext } from '@/helpers/sports/config';
import { SportCompetitionBucketsV2, SportPageType } from '@/interfaces/sport-graphql';

const Language = namespace('language');
const User = namespace('user');

@Component({
	name: 'AllSports',
	components: {
		AdUnit,
		Claim,
		CompetitionList,
		EventCard,
		HiddenHorizontalScrollbar,
		ProvideUpcomingSportEvents,
		SponsoredRecommendationSports,
		SportCard,
		SportHeader,
		SportsLayout,
		SrSportsList,
		Subheading,
	},
	apollo: {
		eventBucketsData: {
			query: GetAllSportsPageV2Document,
			variables(): GetAllSportsPageV2QueryVariables {
				return this.queryVariables;
			},
			update(data) {
				return data;
			},
			skip() {
				return this.$route.name !== 'app.sports.all';
			},
		},
	},
})
export default class AllSports extends Vue {
	eventBucketsData: SportCompetitionBucketsV2;

	ASSETS_DIR = ASSETS_DIR;

	@Language.State webLocale: WebLocale;
	@Language.Getter language: string;
	@Language.Getter country: string;
	@User.Getter isLoggedIn: () => boolean;

	get queryVariables(): GetAllSportsPageV2QueryVariables {
		const startAt = new Date();
		startAt.setUTCHours(0, 0, 0, 0);

		return {
			language: this.language,
			country: this.country,
			platform: Platform.Web,
			bucketSize: 6,
			first: 3,
			filter: {
				startAt: {
					from: startAt.toISOString(),
				},
				packages: [],
			},
			timezoneOffset: 0,
		};
	}

	get isDataLoading() {
		return this.$apollo.queries.eventBucketsData?.loading;
	}

	get miniContext() {
		return {
			sportType: 'GENERIC',
			pageType: SportPageType.AllSports,
		} as const;
	}

	get buckets() {
		const edges = this.eventBucketsData?.sportEventBucketsV2?.edges ?? [];

		if (edges == null || edges.length === 0) return [];

		const buckets = edges.map((bucket, i) => ({
			sport: edges[i].key.sport!.type,
			fullPath: getOverviewUrlBySport(edges[i].key.sport!.type, this.webLocale as SportLocale),
			events: bucket.node?.edges ?? [],
		}));

		buckets.sort((a, b) => this.sportOrder.indexOf(a.sport) - this.sportOrder.indexOf(b.sport));

		return buckets;
	}

	get sportOrder() {
		switch (this.webLocale) {
			case WebLocale.us:
				return [
					SportType.AmericanFootball,
					SportType.Football,
					SportType.Tennis,
					SportType.FormulaRacing,
					SportType.Baseball,
					SportType.Basketball,
					SportType.IceHockey,
					SportType.Cricket,
					SportType.Rugby,
				];
			case WebLocale.ca:
				return [
					SportType.IceHockey,
					SportType.Basketball,
					SportType.Football,
					SportType.Tennis,
					SportType.FormulaRacing,
					SportType.AmericanFootball,
					SportType.Baseball,
					SportType.Cricket,
					SportType.Rugby,
				];
			case WebLocale.uk:
				return [
					SportType.Football,
					SportType.Tennis,
					SportType.Cricket,
					SportType.Rugby,
					SportType.FormulaRacing,
					SportType.Basketball,
					SportType.AmericanFootball,
					SportType.IceHockey,
					SportType.Baseball,
				];
			case WebLocale.in:
				return [
					SportType.Cricket,
					SportType.Football,
					SportType.Tennis,
					SportType.Basketball,
					SportType.FormulaRacing,
					SportType.Rugby,
					SportType.AmericanFootball,
					SportType.Baseball,
					SportType.IceHockey,
				];
			default:
				return [
					SportType.Football,
					SportType.Tennis,
					SportType.FormulaRacing,
					SportType.AmericanFootball,
					SportType.Baseball,
					SportType.Basketball,
					SportType.IceHockey,
					SportType.Cricket,
					SportType.Rugby,
				];
		}
	}

	get esportOrder() {
		return [SportType.EsportsLol, SportType.EsportsDota2, SportType.EsportsCsgo];
	}

	// hide sport event header as long as we only have 1 sport showing
	get showEventHeader() {
		return this.buckets.length > 1;
	}

	getContext(event: SportEventsEdgeV2) {
		if (event.node.sport === SportType.FormulaRacing) {
			return {};
		}
		return eventsEdgeContext(event);
	}
}
